/*
 * State plus a ref mirror updated in the same call, for the flush-then-read pattern around
 * usePendingSubmits: a parent-level Save awaits flushed child-form submits whose updates land
 * in state too late to read in the same pass — React hasn't re-rendered yet when Save proceeds —
 * so the ref half carries the freshest value synchronously while the state half drives renders.
 */
import { MutableRefObject, useCallback, useRef, useState } from 'react';

export const useStateWithRef = <T>(
  initial: T,
): [T, (next: T | ((prev: T) => T)) => void, MutableRefObject<T>] => {
  const [value, setValue] = useState<T>(initial);
  const ref = useRef<T>(initial);

  const set = useCallback((next: T | ((prev: T) => T)) => {
    ref.current = typeof next === 'function' ? (next as (prev: T) => T)(ref.current) : next;
    setValue(ref.current);
  }, []);

  return [value, set, ref];
};
